/**
 * MechEquipmentHistoryDb.js
 *
 * Historical mechanical equipment instances mined from SharePoint estimates
 * and project history. Browse, open source links, and fuzzy-match similar
 * equipment for rough install-hour guidance.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';

const TYPE_OPTIONS = [
  '', 'pump', 'vessel', 'dryer', 'conveyor', 'skid', 'tank', 'fan',
  'compressor', 'heat_exchanger', 'agitator', 'other',
];

const fmt = (n, digits = 1) => {
  if (n == null || n === '') return '—';
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return x.toLocaleString(undefined, { maximumFractionDigits: digits });
};

const MechEquipmentHistoryDb = () => {
  const { instance, accounts } = useMsal();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [equipmentType, setEquipmentType] = useState('');
  const [stats, setStats] = useState(null);
  const [toast, setToast] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [selected, setSelected] = useState(null);
  const [sources, setSources] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showMatch, setShowMatch] = useState(false);
  const [matchForm, setMatchForm] = useState({
    equipment_type: 'pump',
    description: '',
    manufacturer: '',
    model: '',
    size_nominal: '',
    power_hp: '',
    weight_lb: '',
    material: '',
  });
  const [matchResult, setMatchResult] = useState(null);
  const [matchBusy, setMatchBusy] = useState(false);
  const [crawlBusy, setCrawlBusy] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadStats = useCallback(async () => {
    try {
      const r = await authenticatedApiCall(instance, accounts, 'GET', '/mech-history-db/stats');
      if (r.data?.success) setStats(r.data);
    } catch (_) { /* ignore */ }
  }, [instance, accounts]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        page: String(page),
        pageSize: '100',
        sortBy: 'updated_at',
        sortDir: 'DESC',
      });
      if (search) qs.set('search', search);
      if (equipmentType) qs.set('equipment_type', equipmentType);
      const r = await authenticatedApiCall(instance, accounts, 'GET', `/mech-history-db?${qs}`);
      if (r.data?.success) {
        setItems(r.data.data || []);
        setTotal(r.data.total || 0);
      } else {
        showToast(r.data?.message || 'Failed to load', 'error');
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, page, search, equipmentType]);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const openDetail = async (row) => {
    setSelected(row);
    setDetailLoading(true);
    setSources([]);
    try {
      const r = await authenticatedApiCall(instance, accounts, 'GET', `/mech-history-db/${row.id}`);
      if (r.data?.success) {
        setSelected(r.data.data);
        setSources(r.data.sources || []);
      }
    } catch (err) {
      showToast('Failed to load sources', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const runMatch = async () => {
    setMatchBusy(true);
    setMatchResult(null);
    try {
      const body = {
        ...matchForm,
        power_hp: matchForm.power_hp === '' ? null : Number(matchForm.power_hp),
        weight_lb: matchForm.weight_lb === '' ? null : Number(matchForm.weight_lb),
        limit: 12,
      };
      const r = await authenticatedApiCall(instance, accounts, 'POST', '/mech-history-db/match', body);
      if (r.data?.success) setMatchResult(r.data);
      else showToast(r.data?.message || 'Match failed', 'error');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Match failed', 'error');
    } finally {
      setMatchBusy(false);
    }
  };

  const triggerCrawl = async (deep) => {
    setCrawlBusy(true);
    try {
      const r = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/mech-history-db/crawl?deep=${deep ? '1' : '0'}`,
        { deep }
      );
      if (r.data?.success) showToast(r.data.message || 'Crawl queued');
      else showToast(r.data?.message || 'Crawl failed', 'error');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Crawl failed', 'error');
    } finally {
      setCrawlBusy(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / 100));

  return (
    <div style={{ padding: 16, fontFamily: 'Segoe UI, system-ui, sans-serif' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 9999,
          background: toast.type === 'error' ? '#b91c1c' : '#065f46',
          color: '#fff', padding: '10px 14px', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,.2)',
        }}>
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: '#0f766e' }}>Equipment History DB</h2>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 13, maxWidth: 720 }}>
            Historical mechanical equipment instances from Estimates and Project History.
            Each row keeps clickable SharePoint sources. Actual hours from Maestro come later.
          </p>
          {stats && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>
              {fmt(stats.equipment?.n, 0)} equipment · {fmt(stats.equipment?.with_hours, 0)} with install hours ·{' '}
              {fmt(stats.files?.parsed, 0)}/{fmt(stats.files?.n, 0)} files parsed
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setShowMatch(true)} style={btnPrimary}>Find similar</button>
          <button type="button" onClick={() => setShowHelp(true)} style={btnGhost}>❓ Help</button>
          <button type="button" disabled={crawlBusy} onClick={() => triggerCrawl(false)} style={btnGhost}>
            {crawlBusy ? 'Queuing…' : 'Crawl now'}
          </button>
          <button type="button" disabled={crawlBusy} onClick={() => triggerCrawl(true)} style={btnGhost}>
            Deep crawl
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search tag, description, manufacturer, estimate…"
          style={{ ...inputStyle, minWidth: 280, flex: 1 }}
        />
        <select
          value={equipmentType}
          onChange={(e) => { setEquipmentType(e.target.value); setPage(1); }}
          style={inputStyle}
        >
          <option value="">All types</option>
          {TYPE_OPTIONS.filter(Boolean).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div style={{ overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              {['Type', 'Tag', 'Description', 'Mfr / Model', 'Size', 'HP', 'Weight lb', 'Install hrs', 'Estimate', 'Sources', ''].map((h) => (
                <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
                No equipment yet — run a deep crawl to mine SharePoint history.
              </td></tr>
            )}
            {!loading && items.map((row) => (
              <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={td}>{row.equipment_type}</td>
                <td style={td}>{row.tag || '—'}</td>
                <td style={{ ...td, maxWidth: 280 }} title={row.description || ''}>{row.description || '—'}</td>
                <td style={td}>{[row.manufacturer, row.model].filter(Boolean).join(' / ') || '—'}</td>
                <td style={td}>{row.size_nominal || '—'}</td>
                <td style={td}>{fmt(row.power_hp)}</td>
                <td style={td}>{fmt(row.weight_lb, 0)}</td>
                <td style={{ ...td, fontWeight: 600 }}>{fmt(row.est_install_hours)}</td>
                <td style={td}>{row.estimate_code || row.job_number || '—'}</td>
                <td style={td}>{row.source_count ?? '—'}</td>
                <td style={td}>
                  <button type="button" onClick={() => openDetail(row)} style={linkBtn}>Sources</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 13, color: '#475569' }}>
        <span>{total.toLocaleString()} rows</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={btnGhost}>Prev</button>
          <span>Page {page} / {pageCount}</span>
          <button type="button" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)} style={btnGhost}>Next</button>
        </div>
      </div>

      {/* Detail / sources drawer */}
      {selected && (
        <div style={overlay} onClick={() => setSelected(null)}>
          <div style={drawer} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: '#0f766e' }}>
                {selected.tag || selected.equipment_type} — sources
              </h3>
              <button type="button" onClick={() => setSelected(null)} style={btnGhost}>×</button>
            </div>
            <div style={{ fontSize: 13, color: '#334155', marginBottom: 12 }}>
              <div><strong>Description:</strong> {selected.description || '—'}</div>
              <div><strong>Install hrs:</strong> {fmt(selected.est_install_hours)} · <strong>Total hrs:</strong> {fmt(selected.est_total_hours)}</div>
              <div><strong>HP / Weight:</strong> {fmt(selected.power_hp)} / {fmt(selected.weight_lb, 0)} lb</div>
              <div><strong>Pressure:</strong> {fmt(selected.design_pressure)} {selected.pressure_uom || ''} · <strong>Material:</strong> {selected.material || '—'}</div>
              <div><strong>Estimate / Job:</strong> {selected.estimate_code || '—'} / {selected.job_number || '—'}</div>
              <div style={{ color: '#64748b', marginTop: 4 }}>
                Confidence {fmt(selected.parse_confidence, 0)} · method {selected.extraction_method}
                {selected.actual_hours != null ? ` · actual hrs ${fmt(selected.actual_hours)}` : ' · actuals not linked yet'}
              </div>
            </div>
            {detailLoading ? <div>Loading sources…</div> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {sources.length === 0 && <div style={{ color: '#64748b' }}>No source rows recorded.</div>}
                {sources.map((s) => (
                  <div key={s.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, background: '#f8fafc' }}>
                    <div style={{ fontWeight: 600 }}>{s.file_name || 'File'}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{s.folder_path}</div>
                    {(s.sheet_name || s.row_ref || s.page_ref) && (
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        {s.sheet_name && <>Sheet <strong>{s.sheet_name}</strong> </>}
                        {s.row_ref && <>row {s.row_ref} </>}
                        {s.page_ref && <>page {s.page_ref}</>}
                      </div>
                    )}
                    {s.excerpt && <div style={{ fontSize: 12, marginTop: 6, fontStyle: 'italic' }}>“{s.excerpt}”</div>}
                    {Array.isArray(s.fields_contributed) && s.fields_contributed.length > 0 && (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                        Fields: {s.fields_contributed.join(', ')}
                      </div>
                    )}
                    {(s.web_url || s.file_web_url) && (
                      <a
                        href={s.web_url || s.file_web_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'inline-block', marginTop: 8, color: '#0f766e', fontWeight: 600, fontSize: 13 }}
                      >
                        Open in SharePoint ↗
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Find similar */}
      {showMatch && (
        <div style={overlay} onClick={() => setShowMatch(false)}>
          <div style={{ ...drawer, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: '#0f766e' }}>Find similar equipment</h3>
              <button type="button" onClick={() => setShowMatch(false)} style={btnGhost}>×</button>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
              Enter what you know about the new piece. We score historical peers and suggest an install-hour range.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label style={labelStyle}>Type
                <select
                  value={matchForm.equipment_type}
                  onChange={(e) => setMatchForm((f) => ({ ...f, equipment_type: e.target.value }))}
                  style={inputStyle}
                >
                  {TYPE_OPTIONS.filter(Boolean).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label style={labelStyle}>Size
                <input value={matchForm.size_nominal} onChange={(e) => setMatchForm((f) => ({ ...f, size_nominal: e.target.value }))} style={inputStyle} />
              </label>
              <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>Description
                <input value={matchForm.description} onChange={(e) => setMatchForm((f) => ({ ...f, description: e.target.value }))} style={inputStyle} />
              </label>
              <label style={labelStyle}>Manufacturer
                <input value={matchForm.manufacturer} onChange={(e) => setMatchForm((f) => ({ ...f, manufacturer: e.target.value }))} style={inputStyle} />
              </label>
              <label style={labelStyle}>Model
                <input value={matchForm.model} onChange={(e) => setMatchForm((f) => ({ ...f, model: e.target.value }))} style={inputStyle} />
              </label>
              <label style={labelStyle}>Power (HP)
                <input value={matchForm.power_hp} onChange={(e) => setMatchForm((f) => ({ ...f, power_hp: e.target.value }))} style={inputStyle} />
              </label>
              <label style={labelStyle}>Weight (lb)
                <input value={matchForm.weight_lb} onChange={(e) => setMatchForm((f) => ({ ...f, weight_lb: e.target.value }))} style={inputStyle} />
              </label>
              <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>Material
                <input value={matchForm.material} onChange={(e) => setMatchForm((f) => ({ ...f, material: e.target.value }))} style={inputStyle} />
              </label>
            </div>
            <div style={{ marginTop: 12 }}>
              <button type="button" disabled={matchBusy} onClick={runMatch} style={btnPrimary}>
                {matchBusy ? 'Matching…' : 'Match peers'}
              </button>
            </div>
            {matchResult && (
              <div style={{ marginTop: 16 }}>
                <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <strong>Suggested install hours</strong>
                  <div style={{ marginTop: 4 }}>
                    P25 {fmt(matchResult.suggested?.est_install_hours_p25)} ·{' '}
                    Median {fmt(matchResult.suggested?.est_install_hours_median)} ·{' '}
                    P75 {fmt(matchResult.suggested?.est_install_hours_p75)}
                    {' '}({matchResult.suggested?.sample_count || 0} peers with hours)
                  </div>
                </div>
                {(matchResult.peers || []).length === 0 && (
                  <div style={{ color: '#64748b' }}>No peers above the similarity threshold yet — keep crawling.</div>
                )}
                {(matchResult.peers || []).map((p) => (
                  <div key={p.id} style={{ borderBottom: '1px solid #e2e8f0', padding: '8px 0', fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span>
                        <strong>{Math.round((p.similarity || 0) * 100)}%</strong> · {p.tag || p.equipment_type} — {p.description || '—'}
                      </span>
                      <span style={{ whiteSpace: 'nowrap' }}>{fmt(p.est_install_hours)} hrs</span>
                    </div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>
                      {[p.manufacturer, p.model, p.size_nominal, p.estimate_code].filter(Boolean).join(' · ')}
                    </div>
                    <button type="button" style={linkBtn} onClick={() => { setShowMatch(false); openDetail(p); }}>
                      View sources
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Help */}
      {showHelp && (
        <div style={overlay} onClick={() => setShowHelp(false)}>
          <div style={{ ...drawer, maxWidth: 640 }} onClick={(e) => e.stopPropagation()} data-help-root="mech-equipment-history-help">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0, color: '#0f766e' }}>Equipment History DB — Help</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <ListenButton compact guideId="mech-equipment-history-help" title="Equipment History DB Help" accent="#0f766e" dark />
                <button type="button" onClick={() => setShowHelp(false)} style={btnGhost}>×</button>
              </div>
            </div>
            <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.55 }}>
              <HelpWithVideoTour guideId="mech-equipment-history-help" title="Equipment History DB Help" accent="#0f766e">
              <p><strong>What this is.</strong> A living database of mechanical equipment we have estimated before — pumps, vessels, dryers, conveyors, skids, and more — mined from SharePoint Estimates and Project History.</p>
              <p><strong>Sources matter.</strong> Every row remembers the exact SharePoint file (and sheet/row when we have it). If a field like height is missing, open the source and fill the gap.</p>
              <p><strong>Find similar.</strong> Enter type and the specs you know; we return peers with a similarity score and a suggested install-hour range (25th / median / 75th).</p>
              <p><strong>Estimated first.</strong> Hours and costs come from historical estimates. Actual hours from Maestro are planned for a later phase — columns exist but stay empty until that connection is approved.</p>
              <p><strong>Nightly crawl.</strong> An incremental job runs every night; use Deep crawl for a large first fill. Blobs are not stored in Postgres — only pointers and extracted fields.</p>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const td = { padding: '7px 10px', verticalAlign: 'top' };
const inputStyle = {
  padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff',
};
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#475569' };
const btnPrimary = {
  padding: '8px 12px', borderRadius: 6, border: 'none', background: '#0f766e', color: '#fff',
  fontWeight: 600, cursor: 'pointer', fontSize: 13,
};
const btnGhost = {
  padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff',
  color: '#334155', cursor: 'pointer', fontSize: 13,
};
const linkBtn = {
  border: 'none', background: 'none', color: '#0f766e', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 13,
};
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 8000,
  display: 'flex', justifyContent: 'flex-end',
};
const drawer = {
  width: 'min(520px, 100%)', height: '100%', background: '#fff', padding: 20,
  overflow: 'auto', boxShadow: '-8px 0 24px rgba(0,0,0,.15)',
};

export default MechEquipmentHistoryDb;
